import { ScreensPanel } from "./Screens.js";
import { Button, Chip, Field, Panel, Switch } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type SettingRow, type SiteRow } from "../api.js";
import { ErrorLine, useAction } from "./common.js";

const KIND: Record<SiteRow["kind"], string> = {
  kitchen: "آشپزخانه",
  hall: "تالار",
  "serving-point": "محل سرو",
};

/** Kitchens, hall, serving points and their stations — all editable, deactivate keeps history (DL-055). */
export function SitesPage() {
  const sites = useQuery({ queryKey: ["sites"], queryFn: () => api<SiteRow[]>("/sites") });
  const [name, setName] = useState("");
  const [kind, setKind] = useState<SiteRow["kind"]>("kitchen");
  const add = useAction(async () => {
    await api("/sites", { body: { name, kind, active: true } });
    setName("");
  }, [["sites"]]);
  const toggle = useAction(
    (s: SiteRow) =>
      api(`/sites/${s.id}`, {
        method: "PUT",
        body: { name: s.name, kind: s.kind, active: !s.active, version: s.version },
      }),
    [["sites"]],
  );
  const addStation = useAction(
    ({ site, station }: { site: SiteRow; station: string }) =>
      api(`/sites/${site.id}/stations`, { body: { name: station, sortOrder: site.stations.length } }),
    [["sites"]],
  );

  return (
    <div className="stack">
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))" }}>
        {(sites.data ?? []).map((s) => (
          <Panel
            key={s.id}
            title={s.name}
            actions={
              <Chip tone={s.active ? "ok" : undefined} plain={!s.active}>
                {s.active ? "فعال" : "غیرفعال"}
              </Chip>
            }
          >
            <div className="stack">
              <span className="muted">{KIND[s.kind]}</span>
              <div className="row wrap" style={{ gap: "var(--s2)" }}>
                {s.stations.map((st) => (
                  <Chip key={st.id} plain>
                    {st.name}
                  </Chip>
                ))}
                {s.stations.length === 0 && <span className="t-cap muted">ایستگاهی ندارد</span>}
              </div>
              <StationAdder
                onAdd={(station) => {
                  addStation.run({ site: s, station });
                }}
              />
              <Switch
                label="فعال"
                checked={s.active}
                onChange={() => {
                  toggle.run(s);
                }}
              />
            </div>
          </Panel>
        ))}
      </div>
      <Panel title="محل جدید">
        <form
          className="row wrap"
          style={{ alignItems: "flex-end" }}
          onSubmit={(e) => {
            e.preventDefault();
            add.run(undefined);
          }}
        >
          <Field
            id="site-name"
            label="نام"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <div className="field">
            <label htmlFor="site-kind">نوع</label>
            <select
              id="site-kind"
              className="input"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as SiteRow["kind"]);
              }}
            >
              {Object.entries(KIND).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="primary" icon="plus" disabled={!name || add.busy}>
            افزودن
          </Button>
        </form>
        <ErrorLine error={add.error ?? toggle.error ?? addStation.error} />
      </Panel>
    </div>
  );
}

function StationAdder({ onAdd }: { onAdd: (name: string) => void }) {
  const [v, setV] = useState("");
  return (
    <form
      className="row"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.trim()) {
          onAdd(v.trim());
          setV("");
        }
      }}
    >
      <input
        className="input"
        aria-label="نام ایستگاه تازه"
        placeholder="ایستگاه تازه"
        value={v}
        onChange={(e) => {
          setV(e.target.value);
        }}
      />
      <Button type="submit" icon="plus">
        افزودن
      </Button>
    </form>
  );
}

/** Manager-editable policies registered by modules (identity idle locks, re-verification, …). */
export function SettingsPage() {
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => api<SettingRow[]>("/settings") });
  return (
    <div className="stack">
      <ScreensPanel />
      <Panel title="تنظیمات">
        <div className="stack">
          {(settings.data ?? []).map((s) => (
            <SettingEditor key={s.key} row={s} />
          ))}
        </div>
      </Panel>
    </div>
  );
}

function SettingEditor({ row }: { row: SettingRow }) {
  const [value, setValue] = useState(String(row.value));
  const save = useAction(async () => {
    const parsed: unknown = typeof row.default === "number" ? Number(value) : value;
    // The version guards against overwriting a change made meanwhile (the server answers 409 with a message).
    await api(`/settings/${row.key}`, {
      method: "PUT",
      body: {
        scope: { kind: "global" },
        value: parsed,
        ...(row.version === null ? {} : { version: row.version }),
      },
    });
  }, [["settings"]]);
  return (
    <div className="card row wrap" style={{ alignItems: "flex-end" }}>
      <Field
        id={`s-${row.key}`}
        label={row.label}
        value={value}
        inputMode="numeric"
        dir="ltr"
        onChange={(e) => {
          setValue(e.target.value);
        }}
        hint={`پیش‌فرض: ${String(row.default)}`}
      />
      <Button
        onClick={() => {
          save.run(undefined);
        }}
        disabled={save.busy || value === String(row.value)}
      >
        ذخیره
      </Button>
      <ErrorLine error={save.error} />
    </div>
  );
}
